import { Family } from '../models/Family';
import { OrganizationMembership } from '../models/OrganizationMembership';

import { FAMILY_URL } from 'common/api/config';
import { APIResponse } from 'common/api/models';
import {
    HomeDetailsResponse,
    DefaultOrganization,
} from 'common/api/crm/home/models/HomeDetailsResponse';
import { withTokensIfNeeded } from 'common/api/utils/withTokensIfNeeded';
import { delay } from 'common/utils';

// After enrollment the CRM `family`, org membership, and `default-organization` records are written
// asynchronously by a service bus consumer across three separate commits, so they can briefly 404
// (or, for the membership list, come back empty) while it catches up. We retry the fetch with
// backoff (rather than surfacing a fatal error that redirects to the faileddefaultorg screen) so the
// web tolerates the settle window the same way the native app does.
// 7 attempts with the backoff below ≈ 7.75s of total wait.
const MAX_SETTLE_ATTEMPTS = 7;
// The "family written but no org at all yet" shape is indistinguishable from a genuinely org-less
// user, so we cap how long we retry it. Org-less is very rare, so this can be generous: 5 attempts
// ≈ 3.75s of total wait.
const MAX_NO_ORG_SETTLE_ATTEMPTS = 5;
const INITIAL_SETTLE_DELAY_MS = 250;
const MAX_SETTLE_DELAY_MS = 2000;

const isNotFound = <T, E>(res: APIResponse<T, E>) => res.error?.response?.status === 404;

function buildOrgMap(orgList: OrganizationMembership[]) {
    return orgList.reduce(
        (orgs, orgSummary) => ({
            ...orgs,
            [orgSummary.organization.id]: orgSummary,
        }),
        {}
    );
}

//TODO: this call should be refactored into indivdual API calls so that error handling can be
// managed on a per call basis.
export const _getHomeDetails = async (): Promise<APIResponse<HomeDetailsResponse>> => {
    let settleDelayMs = INITIAL_SETTLE_DELAY_MS;

    for (let attempt = 1; ; attempt++) {
        const [familyResponse, defaultOrgResponse, orgsResponse] = await Promise.all([
            withTokensIfNeeded<Family>(`${FAMILY_URL}?api-version=1`),
            withTokensIfNeeded<HomeDetailsResponse['defaultOrganization']>(
                `${FAMILY_URL}/organization/default?api-version=1`
            ),
            withTokensIfNeeded<OrganizationMembership[]>(
                `${FAMILY_URL}/organizations?api-version=1`
            ),
        ]);

        // These shapes mean the CRM records are still settling after enrollment, so retry with
        // backoff before giving up. (A non-404 error on family is a real failure, not settling, so
        // it is never retried here.)
        const familyOk = !familyResponse.error;
        const defaultOrgMissing = isNotFound(defaultOrgResponse);
        const belongsToOrg = (orgsResponse.data?.length ?? 0) > 0;
        const orgsNotFound = isNotFound(orgsResponse);
        // A definitive empty membership list (200 with []) — distinct from a 404 read failure.
        const orgsEmpty = !orgsResponse.error && !belongsToOrg;

        // Strong settle signals (full budget): family not written yet; membership present but
        // default-org not yet; or the membership list itself still 404ing. A genuinely org-less user
        // gets an empty 200 from the memberships endpoint, never a 404, so an orgs 404 is transient.
        const settling =
            isNotFound(familyResponse) ||
            (familyOk && defaultOrgMissing && (belongsToOrg || orgsNotFound));

        // Ambiguous signal (short budget): family written, no default org, and a definitive empty
        // membership list. Indistinguishable from a genuinely org-less user, so retried only briefly
        // to catch the gap between the family and membership commits without stalling org-less loads.
        const settlingNoOrg = familyOk && defaultOrgMissing && orgsEmpty;

        const shouldRetry =
            (settling && attempt < MAX_SETTLE_ATTEMPTS) ||
            (settlingNoOrg && attempt < MAX_NO_ORG_SETTLE_ATTEMPTS);

        if (shouldRetry) {
            await delay(settleDelayMs);
            settleDelayMs = Math.min(settleDelayMs * 2, MAX_SETTLE_DELAY_MS);
            continue;
        }

        if (familyResponse.error) {
            return familyResponse;
        }

        // 404 is a valid response and we should store null in state for these objects
        // if we get an error other than 404 we want to propagate the error to the AsyncAction
        // portion of state.
        if (defaultOrgResponse.error && defaultOrgResponse.error?.response?.status !== 404) {
            return defaultOrgResponse;
        }
        if (orgsResponse.error && orgsResponse.error?.response?.status !== 404) {
            return orgsResponse;
        }

        const defaultOrgDetails = orgsResponse.data?.find(({ organization }) => {
            if (defaultOrgResponse && defaultOrgResponse.data) {
                return organization.id === defaultOrgResponse.data.id;
            }
        });

        const defaultOrg = {
            ...defaultOrgResponse.data,
            // Default to lower access level if something goes wrong and we don't
            // have the data
            isCoordinator: defaultOrgDetails ? defaultOrgDetails.isCoordinator : false,
        } as DefaultOrganization;

        return {
            data: {
                profile: familyResponse.data,
                selectedOrgId: defaultOrgResponse.data?.id || null,
                defaultOrganization: defaultOrgResponse.data ? defaultOrg : null,
                organizations:
                    orgsResponse && orgsResponse.data ? buildOrgMap(orgsResponse.data) : {},
            },
        };
    }
};
